import { useState } from 'react';
import {
  Paper,
  Stack,
  Typography,
  Box,
  LinearProgress,
  IconButton,
  Collapse,
  CircularProgress,
  useTheme,
} from '@mui/material';
import EmptyCheckBox from '../../svg/EmptyCheckBox';
import OnBoardingCheckbox from '../../svg/OnBoardingCheckbox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Star from '../../svg/star';
import CheckboxOnboard from '../../svg/CheckboxOnboard';
import EmptyCheckboxOnboard from '../../svg/EmptyCheckboxOnboard';
import { useNavigate, useParams } from 'react-router-dom';

interface ChecklistItem {
  label: string;
  checked: boolean;
  points: number;
  path?: string;
}

interface SubSection {
  title: string;
  completed: number;
  total: number;
  items: ChecklistItem[];
}

interface MainSection {
  title: string;
  subsections: SubSection[];
}

const OnboardingSection = ({
  progress = 0,
  detailedData,
  loading,
}: {
  progress: number;
  detailedData: any;
  loading: boolean;
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { userName } = useParams<{ userName?: string }>();

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    Profile: true,
    'Social Media': true,
    Personality: true,
    'Training Documents': true,
    'Training Consistency': true,
    Monetization: true,
    'Soopra Engagement': true,
    'Ask Engagement': true,
  });

  const toggleSection = (title: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const handleNavigation = (subsectionTitle: string) => {
    if (!userName) return;

    switch (subsectionTitle) {
      case 'Profile':
      case 'Personality':
        navigate(`/${userName}/update`);
        break;

      case 'Social Media':
        navigate(`/${userName}/update/social`);
        break;

      case 'Training Documents':
        navigate(`/${userName}/update/documents`);
        break;

      case 'Training Consistency':
        navigate(`/${userName}/update/journaling`);
        break;

      case 'Monetization':
        const monetization = detailedData?.score_breakdown?.monetization;
        const hasSubscription = monetization?.subscription_count > 0;
        const hasCourses = monetization?.courses_count > 0;
        const hasProducts = monetization?.products_count > 0;

        if (!hasSubscription) {
          navigate(`/${userName}/update`);
        } else if (!hasCourses) {
          navigate('/createCourse');
        } else if (!hasProducts) {
          navigate(`/${userName}/products`);
        } else {
          navigate(`/${userName}/dashboard`);
        }
        break;

      case 'Soopra Engagement':
        navigate('/creators');
        break;

      case 'Ask Engagement':
        navigate('/ask');
        break;

      default:
        break;
    }
  };

  const handleItemNavigation = (
    item: ChecklistItem,
    subsectionTitle: string
  ) => {
    if (!userName) return;

    // If item has a specific path, use it
    if (item.path) {
      navigate(item.path);
      return;
    }

    // Otherwise fall back to subsection navigation
    handleNavigation(subsectionTitle);
  };

  const buildMainSections = (): MainSection[] => {
    if (!detailedData?.score_breakdown) {
      return [];
    }

    const breakdown = detailedData.score_breakdown;

    return [
      {
        title: '1. Account',
        subsections: [
          {
            title: 'Profile',
            completed: breakdown.profile.completed_count,
            total: breakdown.profile.total_count,
            items: [
              // Removed email verification item
              {
                label: 'Add full name',
                checked: breakdown.profile.full_name > 0,
                points: 10,
                path: `/${userName}/update`,
              },
              {
                label: 'Add user name',
                checked: breakdown.profile.user_name > 0,
                points: 10,
                path: `/${userName}/update`,
              },
              {
                label: 'Add professional headshot for avatar',
                checked: breakdown.profile.profile_pic > 0,
                points: 10,
                path: `/${userName}/update`,
              },
              {
                label: 'Add biography',
                checked: breakdown.profile.short_bio > 0,
                points: 10,
                path: `/${userName}/update`,
              },
            ],
          },
          {
            title: 'Social Media',
            completed: breakdown.social.completed_count,
            total: breakdown.social.total_count,
            items: [
              {
                label: 'Add LinkedIn',
                checked: breakdown.social.linkedin > 0,
                points: 10,
                path: `/${userName}/update/social`,
              },
              {
                label: 'Add Twitter',
                checked: breakdown.social.twitter > 0,
                points: 10,
                path: `/${userName}/update/social`,
              },
              {
                label: 'Add Instagram',
                checked: breakdown.social.instagram > 0,
                points: 10,
                path: `/${userName}/update/social`,
              },
              {
                label: 'Add TikTok',
                checked: breakdown.social.tiktok > 0,
                points: 10,
                path: `/${userName}/update/social`,
              },
              {
                label: 'Add YouTube',
                checked: breakdown.social.youtube > 0,
                points: 10,
                path: `/${userName}/update/social`,
              },
              {
                label: 'Add Facebook',
                checked: breakdown.social.facebook > 0,
                points: 10,
                path: `/${userName}/update/social`,
              },
            ],
          },
          {
            title: 'Personality',
            completed: breakdown.personality.completed_count,
            total: breakdown.personality.total_count,
            items: [
              {
                label: 'Add age',
                checked: breakdown.personality.age > 0,
                points: 10,
                path: `/${userName}/update`,
              },
              {
                label: 'Add gender',
                checked: breakdown.personality.gender > 0,
                points: 10,
                path: `/${userName}/update`,
              },
              {
                label: 'Add ethnicity',
                checked: breakdown.personality.ethnicity > 0,
                points: 10,
                path: `/${userName}/update`,
              },
              {
                label: 'Add birthplace (city, state, country)',
                checked: breakdown.personality.birth_place > 0,
                points: 10,
                path: `/${userName}/update`,
              },
              {
                label: 'Add residence (city, state, country)',
                checked: breakdown.personality.residence_place > 0,
                points: 10,
                path: `/${userName}/update`,
              },
              {
                label: 'Add occupation',
                checked: breakdown.personality.occupation > 0,
                points: 10,
                path: `/${userName}/update`,
              },
              {
                label: 'Add native language',
                checked: breakdown.personality.native_language > 0,
                points: 10,
                path: `/${userName}/update`,
              },
              {
                label: 'Add training documents',
                checked: breakdown.personality.training_documents > 0,
                points: 10,
                path: `/${userName}/update/documents`,
              },
            ],
          },
        ],
      },
      {
        title: '2. AI Persona',
        subsections: [
          {
            title: 'Training Documents',
            completed: breakdown.training.completed_count,
            total: breakdown.training.total_count,
            items: [
              {
                label: 'Add first document',
                checked: breakdown.training.documents > 0,
                points: 10,
                path: `/${userName}/update/documents`,
              },
              {
                label: 'Add first google drive link',
                checked: breakdown.training.google_drive > 0,
                points: 15,
                path: `/${userName}/update/documents`,
              },
              {
                label: 'Add first journal entry',
                checked: breakdown.training.journaling > 0,
                points: 10,
                path: `/${userName}/update/journaling`,
              },
              {
                label: 'Add first web link',
                checked: breakdown.training.weblink > 0,
                points: 15,
                path: `/${userName}/update/weblinks`,
              },
              {
                label: 'Add first sitemap',
                checked: breakdown.training.sitemap > 0,
                points: 10,
                path: `/${userName}/update/weblinks`,
              },
              {
                label: 'Add Voice Sample',
                checked: breakdown.training.voice_sample > 0,
                points: 10,
                path: `/${userName}/update/voice`,
              },
              {
                label: 'Add YouTube links',
                checked: breakdown.training.youtube_links > 0,
                points: 10,
                path: `/${userName}/update/weblinks`,
              },
              {
                label: 'Add Introduction',
                checked: breakdown.training.introduction > 0,
                points: 10,
                path: `/${userName}/update/introduction`,
              },
              {
                label: 'Add Suggested questions',
                checked: breakdown.training.suggested_questions > 0,
                points: 10,
                path: `/${userName}/update/introduction`,
              },
            ],
          },
          {
            title: 'Training Consistency',
            completed:
              (breakdown.training.edits_count > 0 ? 1 : 0) +
              (breakdown.training.journal_count > 0 ? 1 : 0),
            total: 2,
            items: [
              {
                label: `Dashboard edits (${breakdown.training.edits_count} edits)`,
                checked: breakdown.training.edits_count > 0,
                points: breakdown.training.edits_score,
                path: `/${userName}/update`,
              },
              {
                label: `Journal entries (${breakdown.training.journal_count} entries)`,
                checked: breakdown.training.journal_count > 0,
                points: breakdown.training.journal_score,
                path: `/${userName}/update/journaling`,
              },
            ],
          },
          {
            title: 'Monetization',
            completed: breakdown.monetization.completed_count,
            total: breakdown.monetization.total_count,
            items: [
              {
                label: `Set price card (${breakdown.monetization.subscription_count} subscriptions)`,
                checked: breakdown.monetization.subscription_count > 0,
                points: breakdown.monetization.subscription_score,
                path: `/${userName}/revenue`,
              },
              {
                label: `Create course (${breakdown.monetization.courses_count} courses)`,
                checked: breakdown.monetization.courses_count > 0,
                points: breakdown.monetization.courses_score,
                path: '/createCourse',
              },
              {
                label: `Add product (${breakdown.monetization.products_count} products)`,
                checked: breakdown.monetization.products_count > 0,
                points: breakdown.monetization.products_score,
                path: `/${userName}/products`,
              },
            ],
          },
        ],
      },
      {
        title: '3. Engagement & Referral',
        subsections: [
          {
            title: 'Soopra Engagement',
            completed: breakdown.engagement.completed_count,
            total: breakdown.engagement.total_count,
            items: [
              {
                label: `Receive new follower (${breakdown.engagement.follower_count} followers)`,
                checked: breakdown.engagement.follower_count > 0,
                points: breakdown.engagement.follower_score,
                path: '/creators',
              },
              {
                label: `Receive new chat (${breakdown.engagement.fan_chat_count} chats)`,
                checked: breakdown.engagement.fan_chat_count > 0,
                points: breakdown.engagement.fan_chat_score,
                path: '/creators',
              },
              {
                label: `Start new chat with an AI persona (${breakdown.engagement.persona_chat_count} chats)`,
                checked: breakdown.engagement.persona_chat_count > 0,
                points: breakdown.engagement.persona_chat_score,
                path: '/creators',
              },
              {
                label: `Referrals (${breakdown.engagement.referral_count} referrals)`,
                checked: breakdown.engagement.referral_count > 0,
                points: breakdown.engagement.referral_score,
                path: '/creators',
              },
            ],
          },
          {
            title: 'Ask Engagement',
            completed: breakdown.ask.completed_count,
            total: breakdown.ask.total_count,
            items: [
              {
                label: `New post (${breakdown.ask.posts_count} posts)`,
                checked: breakdown.ask.posts_count > 0,
                points: breakdown.ask.posts_score,
                path: '/ask',
              },
              {
                label: `New comment by your AI agent (${breakdown.ask.comments_count} comments)`,
                checked: breakdown.ask.comments_count > 0,
                points: breakdown.ask.comments_score,
                path: '/ask',
              },
            ],
          },
        ],
      },
    ];
  };

  const mainSections = buildMainSections();

  const calculateSectionTotals = (subsections: SubSection[]) => {
    const completed = subsections.reduce((acc, s) => acc + s.completed, 0);
    const total = subsections.reduce((acc, s) => acc + s.total, 0);
    const progressPercent = total > 0 ? (completed / total) * 100 : 0;
    return { completed, total, progress: progressPercent };
  };

  const overallCompleted =
    detailedData?.score_breakdown?.overall_completed_count ?? 0;
  const overallTotal = detailedData?.score_breakdown?.overall_total_count ?? 0;
  const overallProgress =
    overallTotal > 0 ? (overallCompleted / overallTotal) * 100 : progress;

  if (loading) {
    return (
      <Box
        sx={{
          pt: 4,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 4, width: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          p: 1,
          px: 1.5,
          backgroundColor: isDarkMode
            ? 'var(--background-container-brand-3, #3D4558)'
            : 'var(--background-container-brand-3, #ECEEF9)',
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.25}
          sx={{ width: '100%' }}
        >
          <Typography
            variant='subtitle1'
            fontWeight={600}
            sx={{
              fontSize: '1rem',
              lineHeight: '20.8px',
              letterSpacing: '0.15px',
              fontFamily: 'Merriweather Sans, sans-serif',
              color: isDarkMode
                ? 'var(--text-primary, #FFFFFF)'
                : 'var(--text-primary, rgba(0, 0, 0, 0.87))',
              whiteSpace: 'nowrap',
            }}
          >
            Total Progress
          </Typography>

          <Stack direction='row' alignItems='center' spacing={0.5}>
            {overallProgress === 100 ? (
              <OnBoardingCheckbox />
            ) : (
              <EmptyCheckBox />
            )}
            <Typography
              variant='body2'
              fontWeight={600}
              sx={{
                fontSize: '0.875rem',
                lineHeight: '16.8px',
                letterSpacing: '0.15px',
                fontFamily: 'Merriweather Sans, sans-serif',
                color: isDarkMode
                  ? 'var(--text-primary, #FFFFFF)'
                  : 'var(--text-primary, rgba(0, 0, 0, 0.87))',
              }}
            >
              {overallCompleted}/{overallTotal}
            </Typography>
          </Stack>

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant='determinate'
                value={overallProgress}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: isDarkMode
                    ? 'var(--background-container-0, #1C1C1C)'
                    : 'var(--background-container-0, #FFFFFF)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 2,
                    backgroundColor: isDarkMode
                      ? 'var(--primary-main, #3D87F5)'
                      : 'var(--primary-main, #2542FB)',
                  },
                }}
              />
            </Box>
            <Typography
              variant='body2'
              sx={{
                fontSize: '0.875rem',
                lineHeight: '20.02px',
                letterSpacing: '0.17px',
                minWidth: '40px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                color: isDarkMode
                  ? 'var(--text-secondary, rgba(255, 255, 255, 0.69))'
                  : 'var(--text-secondary, rgba(0, 0, 0, 0.59))',
              }}
            >
              {overallProgress.toFixed(1)}%
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Typography
        sx={{
          color: isDarkMode
            ? 'var(--text-primary, #FFFFFF)'
            : 'var(--text-primary, rgba(0, 0, 0, 0.87))',
          fontSize: '1rem',
          fontFamily: 'Merriweather Sans, sans-serif',
          fontWeight: 600,
          lineHeight: '20.8px',
          letterSpacing: '0.15px',
          mt: 3,
          pl: 1.5,
        }}
      >
        AI Persona Set-Up Checklist
      </Typography>

      <Typography
        sx={{
          color: isDarkMode
            ? 'var(--text-secondary, rgba(255, 255, 255, 0.69))'
            : 'var(--text-secondary, rgba(0, 0, 0, 0.59))',
          fontSize: '0.875rem',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          lineHeight: '20.02px',
          letterSpacing: '0.17px',
          mt: 1,
          pl: 1.5,
          pr: 1.5,
        }}
      >
        Your score is calculated on how much you train and engage with your AI
        persona, as well as social activity across the Soopra platform.
      </Typography>

      {mainSections.map((mainSection) => {
        const sectionTotals = calculateSectionTotals(mainSection.subsections);

        return (
          <Box key={mainSection.title} sx={{ mt: 4 }}>
            <Stack
              direction='row'
              alignItems='center'
              sx={{ width: '100%', flexWrap: 'wrap', gap: 1 }}
            >
              <Typography
                sx={{
                  fontSize: '1.25rem',
                  fontFamily: 'Merriweather Sans, sans-serif',
                  fontWeight: 600,
                  lineHeight: '28px',
                  letterSpacing: '0.15px',
                  color: isDarkMode
                    ? 'var(--text-primary, #FFFFFF)'
                    : 'var(--text-primary, rgba(0, 0, 0, 0.87))',
                  flexShrink: 0,
                }}
              >
                {mainSection.title}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: isDarkMode
                    ? 'var(--background-container-brand-2, #3D4558)'
                    : 'var(--background-container-brand-2, #F0F2FB)',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {sectionTotals.progress === 100 ? (
                    <OnBoardingCheckbox />
                  ) : (
                    <EmptyCheckBox />
                  )}
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontFamily: 'Merriweather Sans, sans-serif',
                      fontWeight: 600,
                      lineHeight: '16.8px',
                      letterSpacing: '0.15px',
                      color: isDarkMode
                        ? 'var(--text-primary, #FFFFFF)'
                        : 'var(--text-primary, rgba(0, 0, 0, 0.87))',
                    }}
                  >
                    {sectionTotals.completed}/{sectionTotals.total}
                  </Typography>
                </Box>

                <Box sx={{ width: 160, position: 'relative', height: 24 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 0,
                      width: '100%',
                      height: 4,
                      backgroundColor: isDarkMode
                        ? 'var(--background-container-0, #1C1C1C)'
                        : 'var(--background-container-0, white)',
                      borderRadius: 2,
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 0,
                      width: `${sectionTotals.progress}%`,
                      height: 4,
                      backgroundColor: isDarkMode
                        ? 'var(--primary-main, #3D87F5)'
                        : 'var(--primary-main, #2542FB)',
                      borderRadius: 2,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: isDarkMode
                    ? 'var(--background-container-brand-2, #3D4558)'
                    : 'var(--background-container-brand-2, #F0F2FB)',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    backgroundColor: 'var(--yellow-700, #FBC02D)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Star />
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    lineHeight: '20.02px',
                    letterSpacing: '0.17px',
                    color: isDarkMode
                      ? 'var(--text-primary, #FFFFFF)'
                      : 'var(--text-primary, rgba(0, 0, 0, 0.87))',
                  }}
                >
                  {sectionTotals.completed}/{sectionTotals.total}
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={2} sx={{ mt: 3, width: '100%' }}>
              {mainSection.subsections.map((subsection) => {
                const isExpanded = expandedSections[subsection.title] ?? true;
                const hasIncompleteItems = subsection.items.some(
                  (item) => !item.checked
                );

                return (
                  <Box key={subsection.title} sx={{ width: '100%' }}>
                    <Paper
                      elevation={0}
                      onClick={() => {
                        if (hasIncompleteItems) {
                          // Find first incomplete item and navigate to its path
                          const firstIncompleteItem = subsection.items.find(
                            (item) => !item.checked
                          );
                          if (firstIncompleteItem) {
                            handleItemNavigation(
                              firstIncompleteItem,
                              subsection.title
                            );
                          } else {
                            handleNavigation(subsection.title);
                          }
                        } else {
                          toggleSection(subsection.title);
                        }
                      }}
                      sx={{
                        backgroundColor: isDarkMode
                          ? 'var(--background-paper-elevation-1, #2C2C2C)'
                          : 'var(--background-paper-elevation-1, #FFFFFF)',
                        borderRadius: 3,
                        border: `1px solid ${
                          isDarkMode
                            ? 'rgba(255, 255, 255, 0.23)'
                            : 'rgba(0, 0, 0, 0.23)'
                        }`,
                        px: { xs: 2, sm: 3 },
                        py: 1.25,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: isDarkMode
                            ? 'var(--background-paper-elevation-2, #383838)'
                            : 'var(--background-paper-elevation-2, #F5F5F5)',
                        },
                      }}
                    >
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                      >
                        <Typography
                          sx={{
                            fontSize: '1rem',
                            fontFamily: 'Merriweather Sans, sans-serif',
                            fontWeight: 600,
                            lineHeight: '20.8px',
                            letterSpacing: '0.15px',
                            color: isDarkMode
                              ? 'var(--text-primary, #FFFFFF)'
                              : 'var(--text-primary, rgba(0, 0, 0, 0.87))',
                          }}
                        >
                          {subsection.title}
                        </Typography>

                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Typography
                            sx={{
                              fontSize: '1rem',
                              fontFamily: 'Inter, sans-serif',
                              color: isDarkMode
                                ? 'var(--text-secondary, rgba(255, 255, 255, 0.7))'
                                : 'var(--text-secondary, rgba(0, 0, 0, 0.6))',
                            }}
                          >
                            {subsection.completed}/{subsection.total}
                          </Typography>
                          <IconButton
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSection(subsection.title);
                            }}
                            sx={{
                              color: isDarkMode
                                ? 'rgba(255, 255, 255, 0.56)'
                                : 'rgba(0, 0, 0, 0.56)',
                            }}
                          >
                            {isExpanded ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Paper>

                    <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                      <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
                        {subsection.items.map((item, index) => (
                          <Stack
                            key={`${subsection.title}-${index}`}
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            sx={{
                              py: 1.5,
                              borderBottom:
                                index < subsection.items.length - 1
                                  ? `1px solid ${
                                      isDarkMode
                                        ? 'rgba(255, 255, 255, 0.08)'
                                        : 'rgba(0, 0, 0, 0.08)'
                                    }`
                                  : 'none',
                            }}
                          >
                            <Stack
                              direction='row'
                              alignItems='center'
                              spacing={1.5}
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                cursor: !item.checked ? 'pointer' : 'default',
                              }}
                              onClick={() => {
                                if (!item.checked) {
                                  handleItemNavigation(item, subsection.title);
                                }
                              }}
                            >
                              <Box sx={{ flexShrink: 0 }}>
                                {item.checked ? (
                                  <CheckboxOnboard />
                                ) : (
                                  <EmptyCheckboxOnboard />
                                )}
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: '0.875rem',
                                  fontFamily: 'Inter, sans-serif',
                                  fontWeight: 400,
                                  lineHeight: '20.02px',
                                  letterSpacing: '0.17px',
                                  color: item.checked
                                    ? isDarkMode
                                      ? 'rgba(255, 255, 255, 0.38)'
                                      : 'rgba(0, 0, 0, 0.38)'
                                    : isDarkMode
                                      ? 'var(--primary-light, #6B9EF7)'
                                      : 'var(--primary-main, #2542FB)',
                                  textDecoration: item.checked
                                    ? 'none'
                                    : 'underline',
                                  '&:hover': {
                                    opacity: !item.checked ? 0.8 : 1,
                                  },
                                }}
                              >
                                {item.label}
                              </Typography>
                            </Stack>

                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 400,
                                color: item.checked
                                  ? isDarkMode
                                    ? 'rgba(255, 255, 255, 0.38)'
                                    : 'rgba(0, 0, 0, 0.38)'
                                  : isDarkMode
                                    ? 'var(--text-secondary, rgba(255, 255, 255, 0.7))'
                                    : 'var(--text-secondary, rgba(0, 0, 0, 0.6))',
                                flexShrink: 0,
                                ml: 2,
                              }}
                            >
                              {item.points} pts
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export default OnboardingSection;
